'use client';

import {
  CheckCircle,
  CircleNotch,
  Info,
  Warning,
  XCircle,
} from '@phosphor-icons/react/ssr';
import type * as React from 'react';
import { Toaster as Sonner, type ToasterProps } from 'sonner';

/**
 * sonner's Toaster: top-right (Figma stacks bottom-right; moved), at most 3
 * visible (newest in front), 5s by default (paused on hover), 380 wide.
 * Enter: slide down 8px + fade over 200ms ease-out (sonner drops transitions
 * under reduced motion). Figma has one (light) theme. The public `toast`
 * helper (lib/toast.tsx) renders its own Figma card through `toast.custom`;
 * the icons / colours below only apply to plain `sonner` calls.
 */
const Toaster = ({ toastOptions, style, ...props }: ToasterProps) => (
  <Sonner
    theme="light"
    position="top-right"
    visibleToasts={3}
    duration={5000}
    gap={12}
    className="toaster group"
    icons={{
      success: <CheckCircle className="size-4" />,
      info: <Info className="size-4" />,
      warning: <Warning className="size-4" />,
      error: <XCircle className="size-4" />,
      loading: <CircleNotch className="size-4 animate-spin" />,
    }}
    toastOptions={{
      ...toastOptions,
      className: [
        // Enter from 8px above, 200ms ease-out (sonner: 100%, 400ms).
        'data-[mounted=false]:[--y:translateY(-8px)]!',
        '[transition-duration:200ms]! [transition-timing-function:ease-out]!',
        toastOptions?.className,
      ]
        .filter(Boolean)
        .join(' '),
    }}
    style={
      {
        '--width': '380px',
        '--normal-bg': 'var(--color-bg-inverse)',
        '--normal-text': 'var(--color-text-inverse)',
        '--normal-border': 'var(--color-bg-inverse)',
        '--border-radius': 'var(--radius-lg)',
        ...style,
      } as React.CSSProperties
    }
    {...props}
  />
);

export { Toaster, type ToasterProps };
